import * as Sentry from "@sentry/nextjs"
import { ObservableFiniteStateMachine, Identity, OMUniverse } from "@apptivity-lab/firmament-node-sdk"

declare namespace AuthVerificationRequestStateMachine {
    interface Start {
        name: "start"
    }

    interface Presenting {
        name: "presenting"
        identity?: Identity
    }

    interface RequestingCode {
        name: "requestingCode"
        identity: Identity
    }

    interface ShowingError {
        name: "showingError"
        error: Error
        identity: Identity
    }

    interface Completed {
        name: "completed"
        identity?: Identity
    }

    export type State = Start | Presenting | RequestingCode | ShowingError | Completed
}

export class AuthVerificationRequestStateMachine extends ObservableFiniteStateMachine<AuthVerificationRequestStateMachine.State> {

    constructor() {
        super({ name: "start" })
    }

    start() {
        switch (this.state.name) {
            case "start":
                this.state = OMUniverse.shared.currentUser?.isAuthenticated === true
                    ? { name: "completed" }
                    : { name: "presenting" }
                break
            case "presenting":
                this.state = { name: "presenting", identity: this.state.identity }
                break
            default:
                return
        }
    }

    async requestVerificationCode(identity: Identity) {
        switch (this.state.name) {
            case "presenting":
            case "showingError":
                break
            default:
                return
        }

        this.state = { name: "requestingCode", identity }

        // Clear pendingIdentityId from localStorage if user attempts to request
        // verification code again with intent of logging in
        localStorage?.removeItem("pendingIdentityId")

        try {
            OMUniverse.shared.touch(identity)
            await identity.requestVerificationCode()

            this.success()
        } catch (error: unknown) {
            if (error instanceof Error) {
                if (error.message.startsWith("Identity Not Found")) {
                    this.fail(new Error("An account with this email does not exist in the system. Did you mean to register an account instead?"))
                } else {
                    Sentry.captureException(`Magic Link request error - ${error.message}`)
                    this.fail(error)
                }
            } else {
                Sentry.captureException(`Magic Link request error - ${JSON.stringify(error)}`)
                this.fail(new Error("An unknown error has occurred. Please refresh the page and try again."))
            }
        }
    }

    async retry(identity: Identity) {
        switch (this.state.name) {
            case "showingError":
                return this.requestVerificationCode(identity)
            default:
                return
        }
    }

    private success() {
        switch (this.state.name) {
            case "requestingCode":
                this.state = { name: "completed", identity: this.state.identity }
                break
            default:
                return
        }
    }

    private fail(error: Error) {
        switch (this.state.name) {
            case "requestingCode":
                this.state = { name: "showingError", error, identity: this.state.identity }
                break
            default:
                return
        }
    }
}
